// Port of utilities/drop_classifier.py + views/drop_overlay.py for the browser.
import { OPEN_SUFFIXES, TEXTURE_REPLACE_SUFFIXES } from "./options.js";
import { escapeHtml, hasUnityFsHeader, suffix, suffixInContainer } from "./util.js";

export const DropAction = { OPEN: "open", REPLACE: "replace", REPLACE_CONFIRM: "replace_confirm", REJECT: "reject" };
const MAX_VISIBLE_FILES = 5;

function displayNames(files) {
  let names = files.slice(0, 3).map((f) => f.name).join(", ");
  if (files.length > 3) names += ` (+${files.length - 3})`;
  return names;
}

async function isOpenFile(file) {
  if (OPEN_SUFFIXES.includes(suffix(file.name))) return true;
  return hasUnityFsHeader(file);
}

/** Final decision once file names and bytes are available (on drop). */
export async function classifyDrop(files, selected, canReplace) {
  if (!files.length) return { action: DropAction.REJECT, files, title: "Cannot drop", detail: "No files" };
  const names = displayNames(files);
  const flags = await Promise.all(files.map(isOpenFile));
  if (flags.every(Boolean)) return { action: DropAction.OPEN, files, title: "Open Asset Bundles", detail: names };
  if (flags.some(Boolean)) {
    return { action: DropAction.REJECT, files, title: "Cannot drop", detail: "Do not mix bundle files with replacement files" };
  }
  if (!canReplace || !selected) {
    return { action: DropAction.REJECT, files, title: "Cannot drop", detail: "Select one replaceable asset first" };
  }
  if (selected.type === "Texture2D") {
    if (files.every((f) => TEXTURE_REPLACE_SUFFIXES.includes(suffix(f.name)))) {
      return { action: DropAction.REPLACE, files, title: "Replace", detail: `${names} → ${selected.name}` };
    }
    return { action: DropAction.REJECT, files, title: "Cannot drop", detail: `${names} is not an image for Texture2D` };
  }
  if (selected.type === "TextAsset") {
    const container = selected.container || "";
    const matched = files.every((f) => suffixInContainer(f.name, container));
    return { action: matched ? DropAction.REPLACE : DropAction.REPLACE_CONFIRM, files, title: "Replace", detail: `${names} → ${selected.name}` };
  }
  return { action: DropAction.REJECT, files, title: "Cannot drop", detail: `Replace is not supported for ${selected.type}` };
}

/**
 * Best guess while dragging. Browsers hide file names until drop, so only the
 * MIME types and the item count are known here.
 */
export function previewDecision(dataTransfer, selected, canReplace) {
  const items = Array.from(dataTransfer?.items || []).filter((i) => i.kind === "file");
  if (!items.length) return null;
  const n = items.length;
  const label = `${n} file${n > 1 ? "s" : ""}`;
  const images = items.every((i) => i.type.startsWith("image/"));
  const anyImage = items.some((i) => i.type.startsWith("image/"));
  if (images) {
    if (canReplace && selected?.type === "Texture2D") return { action: DropAction.REPLACE, title: "Replace", lines: [[label, selected.name]] };
    if (canReplace && selected?.type === "TextAsset") return { action: DropAction.REPLACE, title: "Replace", lines: [[label, selected.name]] };
    return { action: DropAction.REJECT, title: "Cannot drop", lines: ["Select one replaceable asset first"] };
  }
  if (anyImage) return { action: DropAction.REJECT, title: "Cannot drop", lines: ["Do not mix bundle files with replacement files"] };
  if (canReplace && selected?.type === "TextAsset") {
    return { action: DropAction.OPEN, title: "Open or Replace", lines: [`${label}`, "Bundles open · other files replace " + selected.name] };
  }
  return { action: DropAction.OPEN, title: "Open Asset Bundles", lines: [label] };
}

export class DropOverlay {
  constructor(el) {
    this.el = el;
    this.title = el.querySelector(".do-title");
    this.body = el.querySelector(".do-body");
    this.key = null;
  }

  show(decision) {
    const key = JSON.stringify(decision);
    if (this.key === key && this.el.classList.contains("show")) return;
    this.key = key;
    const style = decision.action === DropAction.REPLACE_CONFIRM ? DropAction.REPLACE : decision.action;
    this.el.className = `drop-overlay show ${style}`;
    this.title.textContent = decision.title;
    const lines = decision.lines || [];
    const shown = lines.slice(0, MAX_VISIBLE_FILES);
    if (lines.length > MAX_VISIBLE_FILES) shown.push(`+ ${lines.length - MAX_VISIBLE_FILES} more`);
    this.body.innerHTML = shown
      .map((line) => (Array.isArray(line) ? `<div>${escapeHtml(line[0])} → ${escapeHtml(line[1])}</div>` : `<div>${escapeHtml(line)}</div>`))
      .join("");
  }

  clear() {
    this.key = null;
    this.el.className = "drop-overlay";
  }
}

/** Collect File objects from a drop, expanding dropped folders (top level only, bundles only). */
export async function filesFromDrop(dataTransfer) {
  // Everything is read from the DataTransfer before the first await: it is
  // emptied once the drop event is over.
  const items = Array.from(dataTransfer.items || []).filter((i) => i.kind === "file");
  const entries = items.map((i) => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null));
  const plain = Array.from(dataTransfer.files || []);
  const asFiles = items.map((i) => i.getAsFile());
  // Chromium also gives file system handles: kept with the files (`file.handle`),
  // they let the tab read the files again after a reload (see files.js).
  const handles = items.map((i) => (i.getAsFileSystemHandle ? i.getAsFileSystemHandle().catch(() => null) : Promise.resolve(null)));
  const withHandle = (file, handle) => {
    if (file && handle?.kind === "file" && handle.name === file.name) file.handle = handle;
    return file;
  };
  if (!entries.some((e) => e?.isDirectory)) {
    const got = await Promise.all(handles);
    return plain.map((f, i) => withHandle(f, got[i]));
  }
  const files = [];
  for (const [index, entry] of entries.entries()) {
    const handle = await handles[index];
    if (!entry) {
      if (asFiles[index]) files.push(withHandle(asFiles[index], handle));
    } else if (entry.isFile) {
      files.push(withHandle(await new Promise((res, rej) => entry.file(res, rej)), handle));
    } else if (entry.isDirectory) {
      const children = new Map();
      if (handle?.kind === "directory") {
        try {
          for await (const h of handle.values()) if (h.kind === "file") children.set(h.name, h);
        } catch {
          // No handles for this folder: its files open all the same.
        }
      }
      const reader = entry.createReader();
      let batch;
      do {
        batch = await new Promise((res, rej) => reader.readEntries(res, rej));
        for (const child of batch) {
          if (child.isFile && OPEN_SUFFIXES.includes(suffix(child.name))) {
            files.push(withHandle(await new Promise((res, rej) => child.file(res, rej)), children.get(child.name)));
          }
        }
      } while (batch.length);
    }
  }
  return files;
}
